import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { parseISO, format, addWeeks, addMinutes } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function AssignTeacher() {
  const [loading, setLoading] = useState(true);
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [schedule, setSchedule] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [error, setError] = useState(null);
  const [gender, setGender] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const userId = location.state?.userId;

  useEffect(() => {
    if (!userId) {
      setError('No user ID provided');
      setLoading(false);
      return;
    }
    fetchTeachers();
  }, [userId]);

  const fetchTeachers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('teachers')
        .select('id, full_name');
      
      if (error) throw error;
      
      setTeachers(data || []);
    } catch (error) {
      console.error('Error fetching teachers:', error.message);
      setError('Failed to fetch teachers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleChange = (day, time, consecutiveWeeks, className, duration) => {
    const updatedSchedule = schedule.filter(item => item.day !== day);
    if (time) {
      updatedSchedule.push({ 
        day, 
        time, 
        consecutive_weeks: parseInt(consecutiveWeeks) || 1,
        class_name: className || 'Untitled Class',
        duration: parseInt(duration) || 60
      });
    }
    setSchedule(updatedSchedule);
  };

  const generateJitsiLink = () => {
    const roomName = uuidv4();
    return `https://meet.jit.si/${roomName}`;
  };

  const handleAssignTeacher = async (e) => {
    e.preventDefault();
    if (!selectedTeacher || schedule.length === 0 || !startDate || !userId || !gender || !paymentDate || !paymentAmount) {
      setError('Please fill in all required fields');
      return;
    }
    setLoading(true);
    setError(null);
    
    try {
      const jitsiLink = generateJitsiLink();

      const { data, error } = await supabase
        .from('students')
        .update({ 
          teacher_id: selectedTeacher,
          jitsi_link: jitsiLink,
          gender: gender,
          payment_date: paymentDate,
          payment_amount: parseFloat(paymentAmount)
        })
        .eq('id', userId)
        .select();

      if (error) throw error;

      const individualClasses = [];

      for (const item of schedule) {
        const classDate = parseISO(startDate);
        for (let week = 0; week < item.consecutive_weeks; week++) {
          const currentDate = addWeeks(classDate, week);
          while (format(currentDate, 'EEEE') !== item.day) {
            currentDate.setDate(currentDate.getDate() + 1);
          }
          
          const startTime = parseISO(`${format(currentDate, 'yyyy-MM-dd')}T${item.time}`);
          const endTime = addMinutes(startTime, item.duration);

          individualClasses.push({
            student_id: userId,
            teacher_id: selectedTeacher,
            class_name: item.class_name,
            class_date: format(currentDate, 'yyyy-MM-dd'),
            start_time: format(startTime, 'HH:mm:ss'),
            end_time: format(endTime, 'HH:mm:ss'),
            jitsi_link: jitsiLink
          });
        }
      }

      const { error: insertError } = await supabase
        .from('individual_classes')
        .insert(individualClasses);

      if (insertError) throw insertError;

      alert('Teacher assigned and classes scheduled successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error assigning teacher and scheduling classes:', error.message);
      setError(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  if (error) {
    return <div className="flex justify-center items-center h-screen text-red-500">{error}</div>;
  }

  return (
    <div className="max-w-md mx-auto mt-10 p-6 bg-white rounded-lg shadow-xl">
      <form onSubmit={handleAssignTeacher} className="space-y-6">
        <h2 className="text-2xl font-bold mb-6 text-center">Assign a Teacher and Set Schedule</h2>
        
        <div>
          <label htmlFor="teacher-select" className="block text-sm font-medium text-gray-700 mb-1">
            Select a teacher
          </label>
          <select
            id="teacher-select"
            value={selectedTeacher}
            onChange={(e) => setSelectedTeacher(e.target.value)}
            required
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          >
            <option value="">Select a teacher</option>
            {teachers.map((teacher) => (
              <option key={teacher.id} value={teacher.id}>
                {teacher.full_name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-1">
            Gender
          </label>
          <select
            id="gender"
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            required
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          >
            <option value="">Select gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div>
          <label htmlFor="payment-date" className="block text-sm font-medium text-gray-700 mb-1">
            Payment Date
          </label>
          <input
            type="date"
            id="payment-date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            required
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          />
        </div>

        <div>
          <label htmlFor="payment-amount" className="block text-sm font-medium text-gray-700 mb-1">
            Payment Amount
          </label>
          <input
            type="number"
            id="payment-amount"
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
            required
            step="0.01"
            min="0"
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          />
        </div>

        <div>
          <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-1">
            Start Date
          </label>
          <input
            type="date"
            id="start-date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          />
        </div>

        <div className="space-y-2">
          <h3 className="text-lg font-medium text-gray-700">Class Schedule</h3>
          {daysOfWeek.map((day) => (
            <div key={day} className="flex items-center space-x-2">
              <span className="w-24">{day}:</span>
              <input
                type="time"
                onChange={(e) => handleScheduleChange(
                  day, 
                  e.target.value, 
                  schedule.find(s => s.day === day)?.consecutive_weeks || 1,
                  schedule.find(s => s.day === day)?.class_name || '',
                  schedule.find(s => s.day === day)?.duration || 60
                )}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              />
              <input
                type="number"
                min="1"
                placeholder="Weeks"
                onChange={(e) => handleScheduleChange(
                  day, 
                  schedule.find(s => s.day === day)?.time || '', 
                  e.target.value,
                  schedule.find(s => s.day === day)?.class_name || '',
                  schedule.find(s => s.day === day)?.duration || 60
                )}
                className="mt-1 block w-20 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              />
              <input
                type="number"
                min="15"
                step="15"
                placeholder="Duration (min)"
                onChange={(e) => handleScheduleChange(
                  day, 
                  schedule.find(s => s.day === day)?.time || '', 
                  schedule.find(s => s.day === day)?.consecutive_weeks || 1,
                  schedule.find(s => s.day === day)?.class_name || '',
                  e.target.value
                )}
                className="mt-1 block w-32 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              />
              <input
                type="text"
                placeholder="Class Name"
                onChange={(e) => handleScheduleChange(
                  day, 
                  schedule.find(s => s.day === day)?.time || '', 
                  schedule.find(s => s.day === day)?.consecutive_weeks || 1,
                  e.target.value,
                  schedule.find(s => s.day === day)?.duration || 60
                )}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              />
            </div>
          ))}
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-300"
        >
          {loading ? 'Assigning...' : 'Assign Teacher and Schedule Classes'}
        </button>
      </form>
    </div>
  );
}